import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Headphones, Mic, BookOpen, PenTool, ArrowRight, CheckCircle, Users,
  FileText, Monitor, Info, Play, BarChart3,
  Shield, GraduationCap, Sparkles, School, Globe, Star, Zap, FlaskConical
} from "lucide-react";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";
import { MosaicLogoHero } from "@/components/MosaicLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [widaLevels, setWidaLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoStats, setDemoStats] = useState(null);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'student') {
      navigate('/my-assessment', { replace: true });
    }
  }, [user, navigate]);

  // v6.38.12 — Social attribution beacon. If the visitor arrived via a
  // ?utm_source=... link (from a shared social-media post), record one
  // deduped hit per (visitor + source + day) so the Social Media Studio
  // Attribution tab can attribute clicks back to the rep + post that
  // shared it. Best-effort — never blocks the landing page.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const utmSource = p.get('utm_source');
      const utmMedium = p.get('utm_medium');
      if (!utmSource) return;
      axios.post(
        `${API}/attribution/visit`,
        {
          source: utmMedium === 'social' ? 'social' : 'social',
          utm_source: utmSource,
          utm_medium: utmMedium || 'social',
          utm_campaign: p.get('utm_campaign') || '',
          utm_content: p.get('utm_content') || '',
          utm_term: p.get('utm_term') || '',
        },
        { withCredentials: true }
      ).catch(() => {});
    } catch {
      // Attribution must never break the landing page.
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [levelsRes, statsRes] = await Promise.all([
          axios.get(`${API}/wida-levels`),
          axios.get(`${API}/demo-exam/stats`).catch(() => ({ data: null })),
        ]);
        setWidaLevels(levelsRes.data.levels);
        if (statsRes.data) setDemoStats(statsRes.data);
        await axios.post(`${API}/seed-questions`);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const modalities = [
    { icon: Headphones, name: "Listening", count: 24, color: "bg-purple-100 text-purple-700", accent: "border-purple-300", description: "Adaptive audio-based comprehension with visual supports" },
    { icon: Mic, name: "Speaking", count: 12, color: "bg-blue-100 text-blue-700", accent: "border-blue-300", description: "Scaffolded prompts with 3-question task structure" },
    { icon: BookOpen, name: "Reading", count: 24, color: "bg-green-100 text-green-700", accent: "border-green-300", description: "Adaptive text comprehension with graphic supports" },
    { icon: PenTool, name: "Writing", count: 3, color: "bg-orange-100 text-orange-700", accent: "border-orange-300", description: "Constructed response with proficiency rubric scoring" }
  ];

  const socialProof = [
    { stat: "1,020", label: "Unique Questions", sub: "K-12 multicultural" },
    { stat: "7", label: "Grade Bands", sub: "K through 12" },
    { stat: "4", label: "Modalities", sub: "L · S · R · W" },
    { stat: "6", label: "Proficiency Levels", sub: "Entering → Reaching" },
  ];

  return (
    <div className="control-room-layout">
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#001845] via-[#002FA7] to-[#0046E5]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — Text */}
            <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-3 mb-6">
                <MosaicLogoHero />
              </div>

              {/* Eyebrow */}
              <p
                className="text-xs sm:text-sm font-bold text-yellow-300 tracking-[0.2em] uppercase mb-5"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="hero-eyebrow"
              >
                English Language Practice &amp; Assessment
              </p>

              {/* H1 */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.08] tracking-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="hero-headline"
              >
                Help Students Prepare for{' '}
                <span className="text-yellow-400">English Language Proficiency</span> Assessments.
              </h1>

              {/* Value-prop body — MOSAIC first */}
              <p className="text-lg sm:text-xl text-blue-100 mb-5 max-w-xl leading-relaxed" data-testid="hero-body">
                Give multilingual learners meaningful practice across Listening, Speaking, Reading, and Writing. MOSAIC equips teachers with practical assessment tools, instructional resources, scoring, and reports to support language development.
              </p>

              {/* Live Counter */}
              {demoStats && demoStats.total_demos_taken > 0 && (
                <div className="flex items-center gap-2 mb-6 text-sm text-blue-200 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>{demoStats.total_demos_taken.toLocaleString()}+ demo exams taken</span>
                </div>
              )}

              {/* CTAs — Tier-1 hierarchy: Primary / Secondary / Tertiary / Text-link */}
              <div className="mb-5" data-testid="hero-cta-group">
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-start sm:items-center">
                  {/* Primary — Start Assessment (filled, high contrast, dominant) */}
                  <Link to="/grades" className="w-full sm:w-auto">
                    <Button
                      className="h-14 w-full sm:w-auto px-8 text-lg bg-yellow-400 text-gray-900 hover:bg-yellow-300 font-bold border-2 border-yellow-500 transition-all hover:translate-y-[-1px]"
                      style={{ borderRadius: 0 }}
                      data-testid="hero-start-assessment-btn"
                    >
                      Start Assessment
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>

                  {/* Secondary — View Sample Score Report (outlined) */}
                  <Link to="/try-demo" className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="h-14 w-full sm:w-auto px-7 text-lg border-2 border-white/50 text-white hover:bg-white/10 font-semibold transition-all"
                      style={{ borderRadius: 0 }}
                      data-testid="hero-sample-report-btn"
                    >
                      View Sample Score Report
                    </Button>
                  </Link>

                  {/* Tertiary — Request a Quote (ghost / low contrast) */}
                  <Link to="/quote" className="w-full sm:w-auto">
                    <Button
                      variant="ghost"
                      className="h-12 w-full sm:w-auto px-5 text-base text-yellow-200 hover:text-white hover:bg-white/5 font-medium"
                      data-testid="hero-request-quote-btn"
                    >
                      <FileText size={16} className="mr-2" />
                      Request a Quote
                    </Button>
                  </Link>
                </div>

                {/* Text link — Join the Waitlist (lightest treatment) */}
                {(!user || user.role !== 'student') && (
                  <div className="mt-3">
                    <Link
                      to="/join-beta"
                      className="inline-flex items-center gap-1.5 text-sm text-blue-200 hover:text-white underline underline-offset-4 decoration-blue-300/40 hover:decoration-white transition-colors"
                      data-testid="hero-join-beta-link"
                    >
                      <FlaskConical size={14} />
                      Join the pilot waitlist
                    </Link>
                  </div>
                )}
              </div>

              {/* Trust Badges — v2: accurate, defensible wording */}
              <div className="flex flex-wrap gap-4 text-xs text-blue-200 max-w-full min-w-0">
                <Link to="/privacy" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="trust-badge-privacy"><Shield size={14} className="text-green-400" /> FERPA-Aligned Privacy</Link>
                <span className="flex items-center gap-1.5" data-testid="trust-badge-modalities"><CheckCircle size={14} className="text-green-400" /> Four-Modality Framework</span>
                <span className="flex items-center gap-1.5" data-testid="trust-badge-multilingual"><Globe size={14} className="text-green-400" /> Multilingual Support</span>
              </div>
            </div>

            {/* Right — Hero Visual */}
            <div className="opacity-0 animate-fade-in-up hidden lg:block" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Diverse students learning together"
                  className="w-full h-[420px] object-cover border-2 border-white/20"
                />
                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-white border-2 border-black p-4 shadow-lg" data-testid="hero-stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
                      <BarChart3 size={24} />
                    </div>
                    <div>
                      <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>1,020</div>
                      <div className="text-xs text-gray-500 font-semibold">Unique Questions</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white border-2 border-black p-3 shadow-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-yellow-400 text-gray-900 flex items-center justify-center">
                      <Star size={20} />
                    </div>
                    <div>
                      <div className="text-lg font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>K-12</div>
                      <div className="text-xs text-gray-500 font-semibold">All Grades</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SOCIAL PROOF BAR ═══════════ */}
      <section className="bg-gray-900 text-white border-b-2 border-yellow-400">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {socialProof.map((item, i) => (
              <div key={i} className={`opacity-0 animate-fade-in-up`} style={{ animationDelay: `${0.1 + i * 0.1}s`, animationFillMode: 'forwards' }}>
                <div className="text-3xl sm:text-4xl font-black text-yellow-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.stat}
                </div>
                <div className="text-sm font-semibold mt-1">{item.label}</div>
                <div className="text-xs text-gray-400">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ TRY DEMO CTA ═══════════ */}
      <section className="border-b-2 border-gray-200 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 text-sm font-bold mb-6 border border-green-300">
            <Zap size={14} /> NO LOGIN REQUIRED
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            See MOSAIC in Action — <span className="text-[#002FA7]">Right Now</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8 text-lg">
            Pick any grade. Answer 12 language-proficiency questions across Listening, Speaking, Reading, and Writing.
            See a 5-minute practice summary across all four modalities — for classroom use, not an official proficiency determination.
          </p>
          <div className="grid sm:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            {modalities.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.name} className={`border-2 ${m.accent} bg-white p-4 text-center`} data-testid={`demo-cta-modality-${m.name.toLowerCase()}`}>
                  <div className={`w-10 h-10 ${m.color} flex items-center justify-center mx-auto mb-2`}>
                    <Icon size={20} />
                  </div>
                  <div className="font-bold text-sm">{m.name}</div>
                  <div className="text-xs text-gray-600">3 questions</div>
                </div>
              );
            })}
          </div>
          <Link to="/try-demo">
            <Button 
              className="h-14 px-12 text-lg btn-sharp-primary transition-all hover:translate-y-[-1px]"
              data-testid="try-demo-cta-btn"
            >
              <Play size={20} className="mr-2" />
              Start Free Demo — Pick Your Grade
            </Button>
          </Link>
          <p className="text-xs text-gray-400 mt-4">
            No account needed. Your results are private. Request full access afterward.
          </p>
          <div className="mt-6">
            <Link to="/quote">
              <Button 
                variant="outline"
                className="h-12 px-8 text-base border-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-semibold transition-all"
                style={{ borderRadius: 0 }}
                data-testid="demo-request-quote-btn"
              >
                <FileText size={16} className="mr-2" />
                Ready to buy? Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ BUILT FOR TEACHERS BANNER ═══════════ */}
      <section className="bg-gray-900 text-white" data-testid="built-for-teachers-banner">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-14">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 md:mb-10 text-center"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="banner-headline"
          >
            Built for Teachers. <span className="text-yellow-400">Focused on Students.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            <div className="border-l-4 border-yellow-400 pl-5" data-testid="banner-col-teacher-friendly">
              <div className="text-xs sm:text-sm font-bold text-yellow-400 tracking-[0.18em] uppercase mb-2">
                Teacher-Friendly
              </div>
              <div className="text-base md:text-lg text-gray-100 leading-relaxed">
                Organized assessment resources.
              </div>
            </div>
            <div className="border-l-4 border-yellow-400 pl-5" data-testid="banner-col-classroom-ready">
              <div className="text-xs sm:text-sm font-bold text-yellow-400 tracking-[0.18em] uppercase mb-2">
                Classroom-Ready
              </div>
              <div className="text-base md:text-lg text-gray-100 leading-relaxed">
                Practical language activities.
              </div>
            </div>
            <div className="border-l-4 border-yellow-400 pl-5" data-testid="banner-col-instruction-focused">
              <div className="text-xs sm:text-sm font-bold text-yellow-400 tracking-[0.18em] uppercase mb-2">
                Instruction-Focused
              </div>
              <div className="text-base md:text-lg text-gray-100 leading-relaxed">
                Reporting that supports instructional decisions.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ NYC ELD PILOT POSITIONING (v6.32.221) ═══════════ */}
      {/* PRD §6 — MOSAIC is an assessment platform; results route instruction
          back to the curriculum the district already owns. Public copy stays
          ERMA-safe: no endorsements, no logos, no claims about curriculum
          quality. */}
      <section className="border-b border-gray-200 bg-white" data-testid="nyc-eld-positioning">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="border-l-4 border-[#1E3A8A] bg-indigo-50/40 p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1E3A8A] mb-2">
              Built for Your ENL Curriculum — Whatever It Is
            </div>
            <p className="text-base text-gray-800 leading-relaxed">
              Mosaic provides digital and printable language proficiency
              assessments across listening, speaking, reading, and writing.
              Results route instruction back to your existing ENL curriculum,
              whether your district is piloting <span className="font-semibold">Reach Higher</span>,{' '}
              <span className="font-semibold">English 3D</span>, or any other
              program. We're an assessment platform — we don't replace what your
              teachers already use to teach.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════ WIDA STRUCTURE ═══════════ */}
      <section className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Assessment Structure
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Practice assessments across Listening, Speaking, Reading, and Writing, featuring scaffolded tasks and grade-appropriate language activities.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Receptive */}
            <div className="border-2 border-gray-200 bg-white p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">R</span>
                Receptive Domains
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Headphones className="text-purple-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold">Listening</div>
                    <div className="text-sm text-gray-600">"Big Picture" visual context with 3 related questions. Semi-adaptive with TTS audio playback.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="text-green-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold">Reading</div>
                    <div className="text-sm text-gray-600">Passages with graphic supports. Semi-adaptive questions across content areas.</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                <CheckCircle size={14} className="inline mr-1 text-green-600" />
                Auto-scored — Results immediate
              </div>
            </div>
            {/* Productive */}
            <div className="border-2 border-gray-200 bg-white p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-100 text-orange-700 flex items-center justify-center text-sm font-bold">P</span>
                Productive Domains
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mic className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold">Speaking</div>
                    <div className="text-sm text-gray-600">3-question scaffolded tasks with support prompts for lower levels.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <PenTool className="text-orange-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold">Writing</div>
                    <div className="text-sm text-gray-600">Constructed responses scored with a language proficiency writing rubric. Discourse complexity and vocabulary.</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                <Users size={14} className="inline mr-1 text-blue-600" />
                Teacher-scored using proficiency rubrics
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FOR NEW YORK SCHOOLS AND BEYOND (NYSESLAT) ═══════════ */}
      <section className="border-b border-gray-200 bg-blue-50/40" data-testid="ny-nyseslat-section">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-[#002FA7] px-3 py-1 text-xs font-bold mb-4 border border-blue-200 tracking-wider uppercase">
            For New York Schools and Beyond
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Independent classroom practice — for every multilingual learner.
          </h2>
          <p className="text-gray-700 leading-relaxed max-w-3xl text-base">
            MOSAIC provides independent English language classroom practice across Listening, Speaking, Reading, and Writing.
            New York educators can use these activities alongside their instructional preparation for NYSESLAT,
            while educators nationwide can integrate MOSAIC into their existing language-development programs.
          </p>
        </div>
      </section>

      {/* ═══════════ WHY MOSAIC ═══════════ */}
      <section className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Schools Choose MOSAIC
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: GraduationCap, title: "3-Tier Adaptive UI", desc: "K-2 students see larger fonts and dot progress. Grades 3-8 get intermediate layouts. High schoolers get the standard interface. Every student gets age-appropriate design.", color: "bg-purple-100 text-purple-700" },
              { icon: Globe, title: "NYC Multicultural Content", desc: "1,020 questions featuring Dominican, Chinese, Bengali, Haitian, Nigerian, and more NYC communities. Cultural celebrations, subway references, and neighborhood contexts students recognize.", color: "bg-green-100 text-green-700" },
              { icon: BarChart3, title: "Teacher Analytics", desc: "Real-time dashboards showing class-wide and individual proficiency scores. Growth tracking over time, goal setting, cohort comparisons, and printable PDF reports for parent conferences.", color: "bg-blue-100 text-blue-700" },
              { icon: Shield, title: "Privacy & Data Protection", desc: "Role-based access control, audit logging, data retention policies, and secure multi-tenant architecture for district-wide deployments. FERPA-aligned practices documented in our Privacy Policy.", color: "bg-red-100 text-red-700" },
              { icon: Users, title: "Student & Teacher Views", desc: "Students take exams. Teachers see answer keys with scoring rubrics. Admins manage schools and classes. Super Admins control the entire district. Each role sees exactly what they need.", color: "bg-amber-100 text-amber-700" },
              { icon: FileText, title: "PDF Reports & Exports", desc: "Generate printable assessments aligned with standards-based paper assessment format. Student reports, class rosters, multilingual parent guides, and CSV data exports.", color: "bg-cyan-100 text-cyan-700" },
            ].map((f, i) => (
              <div key={i} className="flex gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: `${0.1 + i * 0.1}s`, animationFillMode: 'forwards' }}>
                <div className={`flex-shrink-0 w-12 h-12 ${f.color} flex items-center justify-center`}>
                  <f.icon size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* v6.38.1 — Duplicate inline footer removed. The canonical site footer
          (components/layout/Footer.js) is rendered site-wide by ChromeShell
          in App.js. All essential links/contacts/vendor-code have been
          migrated into that single source of truth. */}
    </div>
  );
};

export default Home;
